<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import WidgetPreview from './WidgetPreview.vue';

const props = defineProps({
  // Array of { id, name, message, colorClass } — strings already resolved.
  notifications: { type: Array, default: () => [] },
  badgeCount: { type: Number, default: 0 },
  open: { type: Boolean, default: false },
  handler: {
    type: String,
    default: 'ai',
    validator: value => ['ai', 'team'].includes(value),
  },
  theme: {
    type: String,
    default: 'bright',
    validator: value => ['bright', 'dark'].includes(value),
  },
  accentColor: { type: String, default: '#6e56cf' },
});

const visibleNotifications = computed(() => props.notifications.slice(-3));
</script>

<template>
  <div class="absolute bottom-6 end-7 flex flex-col items-end gap-3">
    <!-- Incoming notification stack (hidden while the widget is open) -->
    <TransitionGroup
      v-if="!open"
      tag="div"
      class="flex flex-col items-end gap-3"
      move-class="motion-safe:transition-transform motion-safe:duration-300"
      enter-active-class="motion-safe:transition-all motion-safe:duration-300 motion-safe:[transition-timing-function:cubic-bezier(0.34,1.4,0.44,1)]"
      enter-from-class="opacity-0 translate-y-6 scale-90"
      leave-active-class="motion-safe:transition-all motion-safe:duration-200 motion-safe:ease-out"
      leave-to-class="opacity-0 -translate-y-2.5 scale-95"
    >
      <div
        v-for="noti in visibleNotifications"
        :key="noti.id"
        class="flex w-[19.5rem] items-center gap-3 rounded-2xl bg-white px-3.5 py-3 shadow-[0_18px_44px_-20px_rgba(30,20,70,0.5)]"
      >
        <span
          class="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full text-[0.8125rem] font-bold text-white"
          :class="noti.colorClass"
        >
          {{ noti.name.charAt(0) }}
        </span>
        <span class="min-w-0">
          <span
            class="block truncate text-[0.78125rem] font-bold text-[#1b1926]"
          >
            {{ noti.name }}
          </span>
          <span class="block truncate text-xs text-[#6f6c7a]">
            {{ noti.message }}
          </span>
        </span>
      </div>
    </TransitionGroup>

    <!-- Widget preview springs open from the launcher -->
    <Transition
      enter-active-class="origin-bottom-right motion-safe:transition-all motion-safe:duration-500 motion-safe:[transition-timing-function:cubic-bezier(0.34,1.4,0.44,1)]"
      enter-from-class="opacity-0 translate-y-10 scale-[0.1]"
      leave-active-class="origin-bottom-right motion-safe:transition-all motion-safe:duration-200 motion-safe:ease-in"
      leave-to-class="opacity-0 translate-y-6 scale-50"
    >
      <WidgetPreview
        v-if="open"
        :handler="handler"
        :theme="theme"
        :accent-color="accentColor"
      />
    </Transition>

    <!-- Launcher bubble (accent color is user-picked, so style binding is required) -->
    <div
      class="relative grid h-[3.25rem] w-[3.25rem] place-items-center rounded-full shadow-[0_16px_40px_-12px_rgba(30,20,70,0.6)] transition-colors duration-300"
      :style="{ backgroundColor: accentColor }"
    >
      <Icon
        icon="i-ph-chat-circle-fill"
        class="absolute size-6 text-white transition-all duration-300"
        :class="
          open
            ? 'rotate-90 scale-50 opacity-0'
            : 'rotate-0 scale-100 opacity-100'
        "
      />
      <Icon
        icon="i-ph-x-bold"
        class="absolute size-5 text-white transition-all duration-300"
        :class="
          open
            ? 'rotate-0 scale-100 opacity-100'
            : '-rotate-90 scale-50 opacity-0'
        "
      />
      <!-- Unread badge -->
      <Transition
        enter-active-class="motion-safe:transition-transform motion-safe:duration-300 motion-safe:[transition-timing-function:cubic-bezier(0.34,1.4,0.44,1)]"
        enter-from-class="scale-0"
        leave-active-class="motion-safe:transition-transform motion-safe:duration-200"
        leave-to-class="scale-0"
      >
        <span
          v-if="badgeCount > 0 && !open"
          class="absolute -end-1 -top-1 grid h-[1.125rem] min-w-[1.125rem] place-items-center rounded-full border-2 border-white bg-[#e5484d] px-1 text-[0.65625rem] font-bold text-white"
        >
          {{ badgeCount }}
        </span>
      </Transition>
    </div>
  </div>
</template>
